import React from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { ThemeProvider as PounceV2ThemeProvider } from 'pounceV2';
import urls from 'Source/urls';
import { buildUrlsGroup } from 'Pages/Build/Routes';
import { configureUrlsGroup, contentCatalogUrls } from 'Pages/Configure/Routes';
import { investigateUrlsGroup } from 'Pages/Investigate/Routes';
import { getDashboardUrlsGroup } from 'Pages/Dashboard/Routes';
import GuardedRoute from 'Components/GuardedRoute';
import ErrorBoundary from 'Components/ErrorBoundary';
import FallbackLoadingV3 from 'Components/FallbackLoading/FallbackLoadingV3';
import { LayoutV3 } from 'Components/Layout';
import { SettingsShellV3 } from 'Pages/Settings/SettingsShell';
import APIErrorFallbackV3 from 'Components/utils/ApiErrorFallback/ApiErrorFallbackV3';
import AppBootstrapper from 'Components/utils/AppBootstrapper/AppBootstrapper';
import useFeatureToggle, { useFeatureToggleWithLoading } from 'Hooks/useFeatureToggle';
import useCustomDashboardsEnabled from 'Hooks/useCustomDashboardsEnabled';
import LandingRedirectV3 from 'Components/utils/LandingRedirectV3';
import { useAppConfig } from 'Components/utils/AppBootstrapper/AppConfigContext';
import useAuth from 'Hooks/useAuth';
import { expandPermissions } from 'Helpers/utils';
import { Permission } from 'Generated/schema';

// Sign in related pages
const SignInPageV3 = React.lazy(() => import(/* webpackPrefetch: true */ 'Pages/SignIn/SignInV3'));
const SingleSignOnPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/SingleSignOn/SingleSignOnV3'),
);
const ForgotPasswordPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/ForgotPassword/ForgotPasswordV3'),
);
const ForgotPasswordConfirmPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/ForgotPasswordConfirm/ForgotPasswordConfirmV3'),
);
const GitHubAppRegisterPage = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/GitHubAppRegister'),
);

// Routes And Pages
const DashboardRoutes = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Dashboard/Routes'),
);
const ListAlertsPage = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Alerts/ListAlerts/ListAlertsV3'),
);
const AlertDetailsPage = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Alerts/AlertDetails/AlertDetailsV3'),
);
const InvestigateRoutes = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Investigate/Routes/InvestigateRoutes'),
);
const BuildRoutes = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Build/Routes/BuildRoutes'),
);
const ConfigureRoutes = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Configure/Routes/ConfigureRoutes'),
);
const ContentCatalogRoutes = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Configure/Routes/ContentCatalogRoutes'),
);
const LegacyGeneralRedirectV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/General/LegacyGeneralRedirectV3'),
);
const MainInfoPreferencesPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/General/MainInfoPreferencesPageV3'),
);
const ThemePageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/General/ThemePage/ThemePageV3'),
);
const AIPreferencesPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/General/AIPreferencesPageV3'),
);
const InfraCostDashboard = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/General/InfraCostDashboard'),
);
// No webpackPrefetch: this chunk embeds the full OSS license corpus (~7 MB);
// load it on demand only when a user actually opens the page.
const OssLicensesPageV3 = React.lazy(
  () => import('Pages/Settings/About/OssLicenses/OssLicensesPageV3'),
);
const PasswordManagementPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/Auth/PasswordManagementPageV3'),
);
const IdentityAndAccessPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/Auth/IdentityAndAccessPageV3'),
);
const SnowflakeConfigurationPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/DataLake/SnowflakeConfigurationPageV3'),
);
const ScheduledSearchesPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/DataLake/ScheduledSearchesPageV3'),
);
const GitSyncPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/Developer/GitSyncPageV3'),
);
const PantherAnalysisToolPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/Developer/PantherAnalysisToolPageV3'),
);
const ConfigurationPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/AIAutomation/ConfigurationPageV3'),
);
const WebAccessPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/AIAutomation/WebAccessPageV3'),
);
const RemoteMcpPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/AIAutomation/RemoteMcpPageV3'),
);
const SkillsPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/AIAutomation/SkillsPageV3'),
);
const McpServersPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/AIAutomation/McpServersPageV3'),
);
const UsagePageV3 = React.lazy(() => import(/* webpackPrefetch: true */ 'Pages/Settings/Usage'));
const ListRolesPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/UserRoles/ListRoles/ListRolesV3'),
);
const CreateRolePageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/UserRoles/CreateRole/CreateRoleV3'),
);
const EditRolePageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/UserRoles/EditRole/EditRoleV3'),
);
const UsersPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/Users/ListUsersV3'),
);
const APITokenRoutes = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/APIToken'),
);
const SupportPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Support/SupportV3'),
);
const AIPageV3 = React.lazy(() => import(/* webpackPrefetch: true */ 'Pages/AI/AIPageV3'));
const AIHistoryPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/AI/History/AIHistoryPageV3'),
);
const ListScheduledPromptsPage = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/AI/ScheduledPrompts/ListScheduledPromptsV3'),
);
const ScheduledPromptDetailsPage = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/AI/ScheduledPrompts/ScheduledPromptDetailsV3'),
);
const McpOAuthCallbackPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/MCP/McpOAuthCallbackPageV3'),
);
const McpToolSettingsPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/MCP/McpToolPermissionsPageV3'),
);
const ContextTagsPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/Alerts/ContextTags/ContextTagsPageV3'),
);
const CreateAISkillPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/AISkills/CreateAISkill/CreateAISkillV3'),
);
const EditAISkillPageV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Pages/Settings/AISkills/EditAISkill/EditAISkillV3'),
);
// Remote-MCP OAuth approval screen. Mounted under the
// authenticated router so the user must sign in before consenting to an
// MCP authorization. The GuardedRoute writes the encrypted OAuth state
// blob to session storage during the SignIn hop and forwards here.
const McpApprovalPage = React.lazy(() => import(/* webpackPrefetch: true */ 'Pages/McpApproval'));
const Page404V3 = React.lazy(() => import(/* webpackPrefetch: true */ 'Pages/4XX/404V3'));

const PromptControllerV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Components/utils/PromptController/PromptControllerV3'),
);
const AnalyticsTrackerV3 = React.lazy(
  () => import(/* webpackPrefetch: true */ 'Components/utils/AnalyticsTracker/AnalyticsTrackerV3'),
);

// All `/settings/*` routes render inside the unified SettingsShell overlay
// (full viewport, app chrome suppressed). This drives the new IA: a single
// settings surface with a vertical sidebar of categories/sub-items
// replacing the per-feature tab pages.
const SETTINGS_PATH = urls.settings.home();

// Routes that render without the global sidebar/top-nav (e.g. the OAuth consent screen).
// The <Route> stays in AuthenticatedRoutes; LayoutWrapper just skips the LayoutV3 chrome.
const CHROME_FREE_ROUTES = [urls.account.auth.mcpApproval(), urls.contentCatalog.workbench()];

/**
 * Suspense fallback for the AuthenticatedRoutes boundary.
 * `FallbackLoadingV3` is a fixed full-viewport overlay. Chrome-free routes (e.g. the
 * OAuth consent screen) have no persistent shell, so the full-viewport loader is right
 * there. Every other authenticated route renders inside LayoutV3 or the settings shell:
 * keep that chrome mounted and render nothing while the lazy route chunk loads, rather
 * than flashing the overlay over the sidebar/top bar on every cold-chunk navigation.
 */
const AuthenticatedRoutesFallback: React.FC = () => {
  const isChromeFree = useRouteMatch({ path: CHROME_FREE_ROUTES, exact: true });
  return isChromeFree ? <FallbackLoadingV3 /> : null;
};

// Flag-gated settings route. When the tenant flag is off the page
// doesn't exist, so stale bookmarks land on `redirectTo` (defaulting to
// the AI-home redirect, which re-routes AI sub-pages to one the user can
// reach) instead of 404 inside the shell. Must return a `<Route>` or
// `<Redirect>` directly — `<Switch>` inspects `path`/`from` on its
// immediate children.
const flagGatedRoute = (
  enabled: boolean,
  path: string,
  component: React.ComponentType,
  redirectTo: string = urls.settings.ai.home(),
): React.ReactElement =>
  enabled ? (
    <Route exact path={path} component={component} />
  ) : (
    <Redirect exact from={path} to={redirectTo} />
  );

/**
 * Bare `/settings/ai/` redirects to the first AI sub-page the current
 * user can reach, matching the sidebar's top-to-bottom order
 * (Configuration → MCP Servers → Skills). Configuration is the
 * canonical landing, but users with only MCP or Skills permissions
 * would 403 there. Only `*Read` is checked: `expandPermissions`
 * already adds `*Read` when the user holds `*Modify`
 * (`pkg/permissions/dedup.go`).
 *
 * MCP Servers is permission-gated only (matches the legacy AISettings
 * tab — the page handles `ENABLE_AI_MCP_SERVERS` internally), so its
 * fallback is unconditional. Skills IS flag-gated (its legacy tab was),
 * so it's only offered when `ENABLE_AI_SKILLS` is on; the loading-aware
 * hook holds the redirect so a Skills-only user isn't transiently sent
 * to Configuration before the flag resolves. The `userInfo` guard
 * covers the same race before AuthContext populates.
 */
const AISettingsHomeRedirectV3: React.FC = () => {
  const { userInfo } = useAuth();
  const { enabled: isAISkillsEnabled, loading: aiSkillsLoading } =
    useFeatureToggleWithLoading('ENABLE_AI_SKILLS');
  if (aiSkillsLoading || !userInfo) {
    return null;
  }

  const granted = new Set(expandPermissions(userInfo.permissions ?? []));
  const fallbacks: Array<[Permission, () => string]> = [
    [Permission.GeneralSettingsRead, urls.settings.ai.configuration],
    [Permission.McpServerRead, urls.settings.mcp.list],
  ];
  if (isAISkillsEnabled) {
    fallbacks.push([Permission.ViewAiSkills, urls.settings.aiSkills.list]);
  }
  const target = fallbacks.find(([p]) => granted.has(p))?.[1]() ?? urls.settings.ai.configuration();
  return <Redirect to={target} />;
};

const AuthenticatedRoutes = () => {
  const { loading } = useFeatureToggleWithLoading('ENABLE_CUSTOM_DASHBOARDS');
  const enableCustomDashboards = useCustomDashboardsEnabled();
  const isAISkillsEnabled = useFeatureToggle('ENABLE_AI_SKILLS');
  const enableContentCatalog = useFeatureToggle('ENABLE_CONTENT_CATALOG');
  const enableMcpServers = useFeatureToggle('ENABLE_AI_MCP_SERVERS');
  const enableChatHistory = useFeatureToggle('ENABLE_CHAT_HISTORY');
  const enableInfraCostDashboard = useFeatureToggle('INFRA_COST_DASHBOARD');
  const { aiEnableScheduledPrompts } = useAppConfig();

  return (
    <React.Suspense fallback={<AuthenticatedRoutesFallback />}>
      <Switch>
        <Route exact path="/" component={LandingRedirectV3} />
        {/* ******************* DASHBOARD ***************************** */}
        {loading ? (
          <FallbackLoadingV3 />
        ) : (
          <Route
            exact
            path={getDashboardUrlsGroup(enableCustomDashboards)}
            component={DashboardRoutes}
          />
        )}

        {/* ******************* ALERTS ***************************** */}
        <Redirect exact from="/alerts" to={urls.alerts.list()} />
        <Redirect exact from="/alerts/:id" to={urls.alerts.details(':id')} />
        <Route exact path={urls.alerts.list()} component={ListAlertsPage} />
        <Route exact path={urls.alerts.details(':id')} component={AlertDetailsPage} />
        <Route exact path={`${urls.alerts.details(':id')}:section`} component={AlertDetailsPage} />

        {/* ******************* INVESTIGATE ***************************** */}
        <Route exact path={investigateUrlsGroup} component={InvestigateRoutes} />

        {/* ******************* BUILD ***************************** */}
        <Route exact path={buildUrlsGroup} component={BuildRoutes} />

        {/* ******************* CONFIGURE ***************************** */}
        <Route exact path={configureUrlsGroup} component={ConfigureRoutes} />

        {/* ******************* CONTENT CATALOG ***************************** */}
        {enableContentCatalog && (
          <Route exact path={contentCatalogUrls} component={ContentCatalogRoutes} />
        )}

        {/* ******************* AI ***************************** */}
        <Route exact path={urls.ai.home()} component={AIPageV3} />
        {/* AI History — non-exact to support path-based tab routing */}
        {enableChatHistory && <Route path={urls.ai.history.home()} component={AIHistoryPageV3} />}
        {aiEnableScheduledPrompts && (
          <Route
            exact
            path={urls.ai.scheduledPrompts.list()}
            component={ListScheduledPromptsPage}
          />
        )}
        {aiEnableScheduledPrompts && (
          <Route
            exact
            path={urls.ai.scheduledPrompts.details(':id')}
            component={ScheduledPromptDetailsPage}
          />
        )}

        {/* ******************* SETTINGS ***************************** */}
        <Redirect exact from={urls.settings.home()} to={urls.settings.general.mainInfo()} />
        <Route
          exact
          path={urls.settings.general.mainInfo()}
          component={MainInfoPreferencesPageV3}
        />
        {flagGatedRoute(
          enableInfraCostDashboard,
          urls.settings.general.infraDashboard(),
          InfraCostDashboard,
          urls.settings.general.mainInfo(),
        )}
        <Route exact path={urls.settings.general.theme()} component={ThemePageV3} />
        <Route exact path={urls.settings.general.aiPreferences()} component={AIPreferencesPageV3} />
        <Route exact path={urls.settings.about.licenses()} component={OssLicensesPageV3} />
        <Route exact path={urls.settings.auth.password()} component={PasswordManagementPageV3} />
        <Route exact path={urls.settings.auth.identity()} component={IdentityAndAccessPageV3} />
        <Route
          exact
          path={urls.settings.dataLake.snowflake()}
          component={SnowflakeConfigurationPageV3}
        />
        <Route
          exact
          path={urls.settings.dataLake.scheduledSearches()}
          component={ScheduledSearchesPageV3}
        />
        <Route exact path={urls.settings.developer.gitSync()} component={GitSyncPageV3} />
        <Route
          exact
          path={urls.settings.developer.analysisTool()}
          component={PantherAnalysisToolPageV3}
        />
        {/* Legacy `/settings/general/` page is retired. The redirector
            below preserves any `?section=` query param coming from old
            bookmarks or AI deep-links and rewrites it to the matching
            sub-route. Anything unknown falls back to Main Info. */}
        <Route exact path={urls.settings.general.home()} component={LegacyGeneralRedirectV3} />
        <Route exact path={urls.settings.usage()} component={UsagePageV3} />
        {flagGatedRoute(isAISkillsEnabled, urls.settings.aiSkills.create(), CreateAISkillPageV3)}
        {flagGatedRoute(isAISkillsEnabled, urls.settings.aiSkills.edit(':id'), EditAISkillPageV3)}
        {/* Each former tab of `AISettingsV3` is its own page now; the
            legacy non-exact catch-all is gone. */}
        <Route exact path={urls.settings.ai.configuration()} component={ConfigurationPageV3} />
        <Redirect
          exact
          from={urls.settings.ai.alertTriage()}
          to={urls.settings.ai.configuration()}
        />
        <Route exact path={urls.settings.ai.webAccess()} component={WebAccessPageV3} />
        <Route exact path={urls.settings.ai.remoteMcp()} component={RemoteMcpPageV3} />
        {/* MCP Servers is permission-gated only (McpServersPageV3 →
            McpSectionV3 handles ENABLE_AI_MCP_SERVERS internally), so the
            list route is always present — matches the legacy AISettings
            tab, which rendered for any McpServer permission holder. */}
        <Route exact path={urls.settings.mcp.list()} component={McpServersPageV3} />
        {flagGatedRoute(isAISkillsEnabled, urls.settings.aiSkills.list(), SkillsPageV3)}
        <Route exact path={urls.settings.ai.home()} component={AISettingsHomeRedirectV3} />
        <Route exact path={urls.settings.roles.list()} component={ListRolesPageV3} />
        <Route exact path={urls.settings.roles.create()} component={CreateRolePageV3} />
        <Route exact path={urls.settings.roles.edit(':id')} component={EditRolePageV3} />
        <Redirect exact from={urls.settings.roles.details(':id')} to={urls.settings.roles.list()} />
        <Route exact path={urls.settings.users()} component={UsersPageV3} />
        <Route
          path={[urls.settings.api.home(), urls.settings.api.list()]}
          component={APITokenRoutes}
        />
        {/* Legacy Profile Settings page is retired — all five sections now
            live on dedicated sub-routes under the unified shell (Account
            Info on Main Info & Preferences, Password Management under
            Access & Auth, Theme + AI Preferences under General Settings,
            and the alert-assignment / timezone toggles are on Main Info
            with Audit Logs etc.). Bookmark redirects: send any
            `/settings/profile/...` URL to the new Main Info page; users
            navigate from there via the sidebar. */}
        <Redirect from={urls.settings.profile()} to={urls.settings.general.mainInfo()} />
        <Route exact path={urls.settings.alerts.contextTags()} component={ContextTagsPageV3} />
        <Route exact path={urls.account.support()} component={SupportPageV3} />
        {/* Legacy `/settings/mcp/`; hops via `mcp.list()` to the AI
            home when the flag is off. */}
        <Redirect exact from="/settings/mcp/" to={urls.settings.mcp.list()} />
        {flagGatedRoute(enableMcpServers, urls.settings.mcp.callback(), McpOAuthCallbackPageV3)}
        {flagGatedRoute(
          enableMcpServers,
          urls.settings.mcp.tools(':serverId'),
          McpToolSettingsPageV3,
        )}

        {/* ******************* MCP APPROVAL (remote MCP OAuth consent) ***************************** */}
        <Route exact path={urls.account.auth.mcpApproval()} component={McpApprovalPage} />

        <Route component={Page404V3} />
      </Switch>
    </React.Suspense>
  );
};

export const LayoutWrapper: React.FC = () => {
  const { pathname } = useLocation();
  const isChromeFree = useRouteMatch({ path: CHROME_FREE_ROUTES, exact: true });
  const isSettings = useRouteMatch({ path: SETTINGS_PATH });

  const content = (
    <ErrorBoundary resetKey={pathname}>
      <AuthenticatedRoutes />
    </ErrorBoundary>
  );

  if (isChromeFree) {
    return content;
  }
  if (isSettings) {
    return <SettingsShellV3>{content}</SettingsShellV3>;
  }
  return <LayoutV3>{content}</LayoutV3>;
};

// Main page container for the web application, Navigation bar and Content body goes here
const Routes: React.FunctionComponent = () => {
  return (
    <Switch>
      {/* Truly public routes — accessible without authentication */}
      <Route exact path={urls.public.githubAppRegister()}>
        <React.Suspense fallback={<FallbackLoadingV3 />}>
          {/* TODO: Remove PounceV2ThemeProvider once the LIGHT_MODE feature flag is removed and
              UIProviders always provides the pounceV2 theme to all routes. */}
          <PounceV2ThemeProvider>
            <GitHubAppRegisterPage />
          </PounceV2ThemeProvider>
        </React.Suspense>
      </Route>

      <GuardedRoute
        limitAccessTo="anonymous"
        exact
        path={[
          urls.account.auth.signIn(),
          urls.account.auth.singleSignOn(),
          urls.account.auth.forgotPassword(),
          urls.account.auth.resetPassword(),
        ]}
        component={SignInPageV3}
      >
        <React.Suspense fallback={<FallbackLoadingV3 />}>
          {/* Auth pages are always dark regardless of the user's theme preference */}
          <PounceV2ThemeProvider mode="dark">
            <Switch>
              <Route exact path={urls.account.auth.signIn()} component={SignInPageV3} />
              <Route exact path={urls.account.auth.singleSignOn()} component={SingleSignOnPageV3} />
              <Route
                exact
                path={urls.account.auth.forgotPassword()}
                component={ForgotPasswordPageV3}
              />
              <Route
                exact
                path={urls.account.auth.resetPassword()}
                component={ForgotPasswordConfirmPageV3}
              />
            </Switch>
          </PounceV2ThemeProvider>
        </React.Suspense>
      </GuardedRoute>

      <GuardedRoute path="/" limitAccessTo="authenticated">
        <APIErrorFallbackV3>
          <AppBootstrapper fallback={<FallbackLoadingV3 />}>
            <LayoutWrapper />
            <React.Suspense fallback={null}>
              <PromptControllerV3 />
              <AnalyticsTrackerV3 />
            </React.Suspense>
          </AppBootstrapper>
        </APIErrorFallbackV3>
      </GuardedRoute>
    </Switch>
  );
};

export default React.memo(Routes);
